import React, { useState, useEffect } from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { growthTrajectories } from '../../../data/peerData';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, ReferenceLine } from 'recharts';
import { usePanelLink } from '../../../hooks/usePanelLink';

// Central Asia & Silk Road Corridor colors
const countryColors: Record<string, string> = {
  UZB: '#00d26a',  // Primary - Green
  KAZ: '#fbbf24',  // Kazakhstan - Amber
  KGZ: '#00a3ff',  // Kyrgyzstan - Blue
  TJK: '#00d4d4',  // Tajikistan - Cyan
  TKM: '#f97316',  // Turkmenistan - Orange
  AZE: '#ef4444',  // Azerbaijan - Red
  GEO: '#a855f7',  // Georgia - Purple
  MNG: '#6b7280'   // Mongolia - Gray
};

const countryNames: Record<string, string> = {
  UZB: 'Uzbekistan',
  KAZ: 'Kazakhstan',
  KGZ: 'Kyrgyzstan',
  TJK: 'Tajikistan',
  TKM: 'Turkmenistan',
  AZE: 'Azerbaijan',
  GEO: 'Georgia',
  MNG: 'Mongolia'
};

interface GrowthTrajectoriesPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const GrowthTrajectoriesPanel: React.FC<GrowthTrajectoriesPanelProps> = ({ isMinimized, onMinimize }) => {
  const [selectedCountries, setSelectedCountries] = useState<string[]>(['UZB', 'KAZ', 'GEO', 'TJK']);
  const { selectedEntity, isLinked } = usePanelLink('growth-trajectories');

  // When a linked panel selects a country, highlight it by including it in selected countries
  useEffect(() => {
    if (isLinked && selectedEntity?.type === 'country') {
      const countryCode = selectedEntity.id;
      if (countryColors[countryCode]) {
        setSelectedCountries(prev =>
          prev.includes(countryCode) ? prev : [...prev, countryCode]
        );
      }
    }
  }, [selectedEntity, isLinked]);

  const toggleCountry = (code: string) => {
    if (code === 'UZB') return;
    setSelectedCountries(prev =>
      prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );
  };

  // Calculate average growth for each country over the period
  const avgGrowth = (code: string) => {
    const values = growthTrajectories.map(d => d[code as keyof typeof d] as number);
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
  };

  // Calculate COVID resilience (2020 value)
  const covidResilience = (code: string) => {
    const val2020 = growthTrajectories.find(d => d.year === 2020)?.[code as keyof typeof growthTrajectories[0]] as number;
    return val2020;
  };

  // Get linked country for enhanced styling
  const linkedCountry = isLinked && selectedEntity?.type === 'country' ? selectedEntity.id : null;

  return (
    <Panel id="growth-trajectories" title="GDP GROWTH" subtitle="2019-2025" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-green">{avgGrowth('UZB')}%</div>
            <div className="text-xs text-gray-500">Uzbekistan Avg</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className={`text-2xl font-mono font-bold ${covidResilience('UZB') >= 0 ? 'text-data-green' : 'text-data-amber'}`}>
              {covidResilience('UZB') >= 0 ? '+' : ''}{covidResilience('UZB').toFixed(1)}%
            </div>
            <div className="text-xs text-gray-500">COVID Resilience</div>
          </div>
        </div>

        {/* Country Toggle */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {Object.keys(countryColors).map(code => (
            <button
              key={code}
              onClick={() => toggleCountry(code)}
              className={`px-2 py-1 text-xs rounded transition-all ${
                selectedCountries.includes(code)
                  ? 'text-white font-medium border'
                  : 'bg-terminal-bg/40 text-gray-500 hover:text-gray-300 border border-transparent'
              }`}
              style={{
                backgroundColor: selectedCountries.includes(code) ? `${countryColors[code]}20` : undefined,
                borderColor: selectedCountries.includes(code) ? countryColors[code] : undefined,
              }}
              disabled={code === 'UZB'}
            >
              {code}
              {code === 'UZB' && ' ★'}
            </button>
          ))}
        </div>

        {/* Chart */}
        <div className="flex-1 bg-terminal-bg/40 rounded-lg p-3 border border-terminal-border/30 min-h-[140px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={growthTrajectories} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
              <XAxis
                dataKey="year"
                tick={{ fontSize: 9, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
                width={35}
                domain={[-10, 12]}
                tickFormatter={(v) => `${v}%`}
              />
              <ReferenceLine y={0} stroke="#374151" strokeDasharray="3 3" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0a0a0f',
                  border: '1px solid #00d26a',
                  borderRadius: '8px',
                  fontSize: '11px',
                  padding: '10px 14px',
                }}
                labelStyle={{ color: '#ffffff', fontWeight: 'bold', marginBottom: '6px' }}
                formatter={(value: number, name: string) => [
                  `${value.toFixed(1)}%`,
                  countryNames[name] || name
                ]}
              />
              {selectedCountries.map(code => {
                const isLinkedCountry = code === linkedCountry;
                const isUzb = code === 'UZB';
                return (
                  <Line
                    key={code}
                    type="monotone"
                    dataKey={code}
                    stroke={countryColors[code]}
                    strokeWidth={isLinkedCountry ? 3 : isUzb ? 2.5 : 1.5}
                    strokeOpacity={linkedCountry && !isLinkedCountry ? 0.3 : 1}
                    dot={{ r: isLinkedCountry ? 4 : isUzb ? 3 : 2, fill: countryColors[code] }}
                    activeDot={{ r: 5 }}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex justify-center flex-wrap gap-3 mt-2 text-xs">
          {selectedCountries.slice(0, 4).map(code => (
            <div key={code} className="flex items-center gap-1">
              <span className="w-3 h-0.5" style={{ backgroundColor: countryColors[code] }} />
              <span className="text-gray-400">{code}</span>
              <span className="font-mono font-bold" style={{ color: countryColors[code] }}>
                {avgGrowth(code)}%
              </span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.IMF} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default GrowthTrajectoriesPanel;
